import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Divider,
  Modal,
  Stack,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  CircularProgress,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
} from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";

import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import HOST_URL from "../../../utils/Url";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ToastApp from "../../../ToastApp";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { usePermissionData } from "../../../hooks/usePermissionData";

// --- Interfaces ---
interface UserListData {
  srno: number;
  id: string;
  userName: string;
  roleName: string;
}

interface UserPermissionData {
  id: string;
  menuId: number;
  parentId: number;
  menuName: string;
  parentMenuName: string;
  isAdd: boolean;
  isEdit: boolean;
  isDel: boolean;
  isView: boolean;
  isPrint: boolean;
  isExport: boolean;
  isRelease: boolean;
  isPost: boolean;
  UserId: string;
}

interface UserOption {
  useR_ID: string;
  firsT_NAME: string;
  middlE_NAME: string;
  suR_NAME: string;
  ranK_ID: string;
  label?: string;
}

// --- Modal Style ---
const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
  maxWidth: '1200px',
  maxHeight: '90vh',
  bgcolor: "background.paper",
  border: "1px solid #ccc",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  display: 'flex',
  flexDirection: 'column',
};

const modalHeaderStyle = {
  flexShrink: 0,
  borderBottom: 1,
  borderColor: 'divider',
  pb: 2,
  mb: 2,
};

const modalContentStyle = {
  overflowY: "auto",
  flexGrow: 1,
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { bgcolor: '#f1f1f1', borderRadius: '4px' },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: '4px', '&:hover': { bgcolor: '#a5a5a5' } },
};

// --- Helper Functions ---
function createData(
  srno: number, id: string, userName: string, roleName: string
): UserListData {
  return { srno, id, userName, roleName };
}

function createUserPermissionData(
  id: string, menuId: number, parentId: number, menuName: string, parentMenuName: string,
  isAdd: boolean, isEdit: boolean, isDel: boolean, isView: boolean, isPrint: boolean,
  isExport: boolean, isRelease: boolean, isPost: boolean, UserId: string
): UserPermissionData {
  return { id, menuId, parentId, menuName, parentMenuName, isAdd, isEdit, isDel, isView, isPrint, isExport, isRelease, isPost, UserId };
}

// --- Component ---
export default function UserPermissionMaster() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [rows, setRows] = useState<UserListData[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [userPermissions, setUserPermissions] = useState<UserPermissionData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

  // Permission integration
  const { permissionData, loading: permissionLoading } = usePermissionData();

  // --- Data Fetching Callbacks ---
  const getList = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/GetUserPermissionList`, { userId: "-1" });
      if (response.data.isSuccess && Array.isArray(response.data.data)) {
        const mappedData = response.data.data.map((item: any, index: number) =>
          createData(index + 1, item.userId, item.userName, item.roleName)
        );
        setRows(mappedData);
      } else {
        toast.error(t("text.ErrorFetchingData") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
        setRows([]);
      }
    } catch (error) {
      console.error("API call failed (GetUserPermissionList):", error);
      toast.error(t("text.ApiCallFailed"));
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const getEmployeeName = useCallback(async () => {
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}USER/GetUSER`, { useR_ID: "-1" });
      if (response.data.isSuccess && Array.isArray(response.data.data)) {
        const usersWithOptions = response.data.data.map((user: UserOption) => ({
          ...user,
          label: `${user.firsT_NAME || ''} ${user.middlE_NAME || ''} ${user.suR_NAME || ''}`.trim().replace(/\s+/g, ' ') || `User ID: ${user.useR_ID}`
        }));
        setUsers(usersWithOptions);
      } else {
        console.error("Error fetching users:", response.data.mesg);
      }
    } catch (error) {
      console.error("API call failed (GetUSER):", error);
    }
  }, []);

  const getPermissionsForRole = useCallback(async (roleId: string, targetUserId: string) => {
    setIsModalLoading(true);
    setUserPermissions([]);
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}RoleMaster/GetRoleSelectMenu`, { roleId });
      if (response.data.isSuccess && response.data.data?.[0]?.rolePermission) {
        const permissions = response.data.data[0].rolePermission;
        const mappedPermissions = permissions.map((item: any) =>
          createUserPermissionData(
            roleId, item.menuId, item.parentId, item.menuName, item.parentMenuName,
            item.isAdd ?? false, item.isEdit ?? false, item.isDel ?? false,
            item.isView ?? false, item.isPrint ?? false, item.isExport ?? false,
            item.isRelease ?? false, item.isPost ?? false, targetUserId
          )
        );
        setUserPermissions(mappedPermissions);
      } else {
        toast.error(t("text.ErrorFetchingRolePermissions") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
      }
    } catch (error) {
      console.error("API call failed (GetRoleSelectMenu):", error);
      toast.error(t("text.ApiCallFailed"));
    } finally {
      setIsModalLoading(false);
    }
  }, [t]);

  const getPermissionsForUser = useCallback(async (userId: string) => {
    setIsModalLoading(true);
    setUserPermissions([]);
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/GetUserPermissionMaster`, { userId });
      if (response.data.isSuccess && Array.isArray(response.data.data)) {
        const mappedPermissions = response.data.data.map((item: any) =>
          createUserPermissionData(
            item.roleId, item.menuId, item.parentId, item.menuName, item.parentMenuName,
            item.isAdd ?? false, item.isEdit ?? false, item.isDel ?? false,
            item.isView ?? false, item.isPrint ?? false, item.isExport ?? false,
            item.isRelease ?? false, item.isPost ?? false, userId
          )
        );
        const userToEdit = users.find(u => u.useR_ID === userId);
        setSelectedUser(userToEdit || null);
        setUserPermissions(mappedPermissions);
      } else {
        toast.error(t("text.ErrorFetchingUserPermissions") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
      }
    } catch (error) {
      console.error("API call failed (GetUserPermissionMaster):", error);
      toast.error(t("text.ApiCallFailed"));
    } finally {
      setIsModalLoading(false);
    }
  }, [t, users]);

  // --- Initial Load ---
  useEffect(() => {
    if (!permissionLoading) {
      getList();
      getEmployeeName();
    }
  }, [getList, getEmployeeName, permissionLoading]);

  // --- Event Handlers ---
  const handleUserChange = (event: React.SyntheticEvent, newValue: UserOption | null) => {
    setSelectedUser(newValue);
    if (newValue && !editUserId) {
      getPermissionsForRole(newValue.ranK_ID, newValue.useR_ID);
    } else if (!newValue) {
      setUserPermissions([]);
    }
  };

  const handleOpen = (userIdToEdit: string | null = null) => {
    // Check permissions before opening modal
    if (userIdToEdit && !permissionData?.isEdit) {
      toast.error("You don't have permission to edit user permissions");
      return;
    }
    if (!userIdToEdit && !permissionData?.isAdd) {
      toast.error("You don't have permission to add user permissions");
      return;
    }

    setEditUserId(userIdToEdit);
    setSelectedUser(null);
    setUserPermissions([]);
    if (userIdToEdit) {
      setOpen(true);
      getPermissionsForUser(userIdToEdit);
    } else {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUser(null);
    setUserPermissions([]);
    setEditUserId(null);
    setIsModalLoading(false);
  };

  const handleSelectAll = (permissionKey: keyof UserPermissionData, checked: boolean) => {
    if (typeof checked !== 'boolean') return;
    setUserPermissions((prevData) =>
      prevData.map((item) => ({ ...item, [permissionKey]: checked }))
    );
  };

  const handleCheckboxChange = (menuId: number, permissionKey: keyof UserPermissionData) => {
    setUserPermissions((prevData) =>
      prevData.map((item) =>
        item.menuId === menuId ? { ...item, [permissionKey]: !item[permissionKey] } : item
      )
    );
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedUser) {
      toast.warn(t("text.PleaseSelectEmployee"));
      return;
    }
    if (userPermissions.length === 0) {
      toast.warn(t("text.NoPermissionsAssigned"));
      return;
    }

    const payload = {
      userId: selectedUser.useR_ID,
      userPermission: userPermissions,
    };

    setIsModalLoading(true);
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/AddUpdateUserPermissionMaster`, payload);
      if (response.data.isSuccess) {
        toast.success(response.data.mesg || (editUserId ? t("text.PermissionsUpdatedSuccess") : t("text.PermissionsAddedSuccess")));
        handleClose();
        getList();
      } else {
        toast.error(response.data.mesg || t("text.ErrorSavingPermissions"));
      }
    } catch (error) {
      console.error("API call failed (AddUpdateUserPermissionMaster):", error);
      toast.error(t("text.ApiCallFailed"));
    } finally {
      setIsModalLoading(false);
    }
  };

  const handleDeleteRequest = (userId: string) => {
    if (!permissionData?.isDel) {
      toast.error("You don't have permission to delete user permissions");
      return;
    }

    setDeleteUserId(userId);
    confirmDialog({
      message: t("text.ConfirmDeleteUserPermissions"),
      header: t("text.DeleteConfirmation"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: handleDeleteConfirm,
      reject: handleDeleteReject,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteUserId) return;
    try {
      const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/DeleteUserPermissionMaster`, { userId: deleteUserId });
      if (response.data.isSuccess) {
        toast.success(response.data.mesg || t("text.PermissionsDeletedSuccess"));
        getList();
      } else {
        toast.error(response.data.mesg || t("text.ErrorDeletingPermissions"));
      }
    } catch (error) {
      console.error("API call failed (DeleteUserPermissionMaster):", error);
      toast.error(t("text.ApiCallFailed"));
    } finally {
      setDeleteUserId(null);
    }
  };

  const handleDeleteReject = () => {
    toast.info(t("text.DeleteOperationCancelled"), { autoClose: 3000 });
    setDeleteUserId(null);
  };

  // --- DataGrid Columns ---
  const columns: GridColDef<UserListData>[] = [
    { field: "srno", headerName: t("text.SrNo"), minWidth: 80, align: 'center', headerAlign: 'center', filterable: false, sortable: false },
    { field: 'userName', headerName: t('text.UserName'), minWidth: 250, flex: 1 },
    { field: 'roleName', headerName: t('text.RoleName'), minWidth: 200, flex: 1 },
    {
      field: 'actions', headerName: t('text.Action'), minWidth: 130, sortable: false, filterable: false, disableColumnMenu: true, align: 'center', headerAlign: 'center',
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          {permissionData?.isEdit && (
            <Tooltip title={t('text.Edit')} arrow>
              <IconButton onClick={() => handleOpen(params.row.id)} color="primary" size="small" aria-label={t('ariaLabel.Edit', { user: params.row.userName })}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
          {permissionData?.isDel && (
            <Tooltip title={t('text.delete')} arrow>
              <IconButton onClick={() => handleDeleteRequest(params.row.id)} color="error" size="small" aria-label={t('ariaLabel.delete', { user: params.row.userName })}>
                <DeleteIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  // --- Permission Table Columns Definition ---
  const permissionTableColumns: { key: keyof UserPermissionData; label: string }[] = [
    { key: "isAdd", label: t("text.add") }, { key: "isEdit", label: t("text.Edit") }, { key: "isDel", label: t("text.delete") },
    { key: "isView", label: t("text.View") }, { key: "isPrint", label: t("text.Print") }, { key: "isExport", label: t("text.Export") },
    { key: "isRelease", label: t("text.Release") }, { key: "isPost", label: t("text.Post") },
  ];

  // Loading state handling
  if (permissionLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  // --- Render ---
  return (
    <>
      <ToastApp />
      <ConfirmDialog />

      <Paper
        sx={{
          width: "100%", overflow: "hidden", p: { xs: 1, sm: 2, md: 3 }, mt: 2, mb: 2, boxShadow: 3,
          "& .MuiDataGrid-columnHeaders": { bgcolor: `var(--grid-headerBackground, #1976d2)`, color: `var(--grid-headerColor, #fff)`, fontSize: '1rem', fontWeight: '600' },
          "& .MuiDataGrid-toolbarContainer": { padding: '8px', borderBottom: '1px solid', borderColor: 'divider' },
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
          {t("text.UserPermissionMaster")}
        </Typography>

        {/* Add Button - Only show if user has add permission */}
        {permissionData?.isAdd && (
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <Button
              onClick={() => handleOpen()}
              variant="contained"
              startIcon={<AddCircleIcon />}
              sx={{ bgcolor: 'var(--header-background, primary.main)', color: 'var(--header-color, primary.contrastText)', '&:hover': { bgcolor: 'var(--header-background-hover, primary.dark)' } }}
              size="medium"
            >
              {t("text.add")}
            </Button>
          </Stack>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ height: 'auto', width: '100%' }}>
            <DataGrid
              rows={rows} columns={columns} getRowId={(row) => row.id} autoHeight
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              slots={{ toolbar: GridToolbar }}
              slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 }, printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: true } } }}
              sx={{ border: 1, borderColor: 'divider', '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' }, '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' } }}
              density="compact" disableRowSelectionOnClick
            />
          </Box>
        )}
      </Paper>

      {/* --- Add/Edit Modal --- */}
      <Modal open={open} onClose={handleClose} aria-labelledby="user-permission-modal-title">
        <Box sx={modalStyle}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={modalHeaderStyle}>
            <Typography variant="h6" id="user-permission-modal-title" component="h2" sx={{ fontWeight: 'medium' }}>
              {editUserId ? t("text.EditUserPermissions") : t("text.AddUserPermissions")}
            </Typography>
            <IconButton aria-label={t("ariaLabel.closeModal")} onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box sx={modalContentStyle}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} md={6} lg={5}>
                  <Autocomplete
                    id="user-select-autocomplete" options={users} getOptionLabel={(option) => option.label || ''}
                    value={selectedUser} onChange={handleUserChange} isOptionEqualToValue={(option, value) => option.useR_ID === value.useR_ID}
                    renderInput={(params) => (<TextField {...params} label={t("text.SelectEmployeeName")} variant="outlined" size="small" required />)}
                    disabled={!!editUserId || isModalLoading} fullWidth
                  />
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{
                      bgcolor: 'var(--header-background, primary.main)',
                      color: 'var(--header-color, primary.contrastText)',
                      '&:hover': { bgcolor: 'var(--header-background-hover, primary.dark)' },
                      minHeight: '36.5px'
                    }}
                    disabled={isModalLoading || !selectedUser || userPermissions.length === 0}
                  >
                    {isModalLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      editUserId ? t("text.update") : t("text.save")
                    )}
                  </Button>
                </Grid>
                <Grid item xs={6} md={3} lg={2}>
                  <Button type="button" fullWidth variant="outlined" color="secondary" onClick={handleClose} disabled={isModalLoading}>
                    {t("text.cancel")}
                  </Button>
                </Grid>
              </Grid>

              <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'medium' }}>
                {t("text.AssignPermissions")}
              </Typography>

              {isModalLoading && userPermissions.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={1} sx={{ maxHeight: "calc(90vh - 260px)", border: 1, borderColor: 'divider' }}>
                  <Table stickyHeader size="small" aria-label={t('ariaLabel.permissionsTable')}>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: '600', bgcolor: `var(--grid-headerBackground, grey.200)`, color: `var(--grid-headerColor, #000)`, py: 0.5 } }}>
                        <TableCell sx={{ textAlign: 'left', minWidth: 150, zIndex: 1100 }}>{t("text.MenuName")}</TableCell>
                        <TableCell sx={{ textAlign: 'left', minWidth: 150, zIndex: 1100 }}>{t("text.ParentName")}</TableCell>
                        {permissionTableColumns.map((col) => (
                          <TableCell key={col.key} align="center" sx={{ px: { xs: 0.5, sm: 1 }, width: { xs: 65, sm: 80 }, zIndex: 1100 }}>
                            <Tooltip title={t('tooltip.selectAll', { permission: col.label })} arrow placement="top">
                              <Stack direction="column" alignItems="center" spacing={0}>
                                <Typography variant="caption" sx={{ lineHeight: 1.1, mb: -0.5 }}>{col.label}</Typography>
                                <Checkbox
                                  size="small"
                                  checked={userPermissions.length > 0 && userPermissions.every((item) => !!item[col.key])}
                                  indeterminate={userPermissions.length > 0 && !userPermissions.every((item) => !!item[col.key]) && userPermissions.some((item) => !!item[col.key])}
                                  onChange={(e) => handleSelectAll(col.key as keyof UserPermissionData, e.target.checked)}
                                  inputProps={{ 'aria-label': t('ariaLabel.selectAllPermission', { permission: col.label }) }}
                                  sx={{ p: 0.5 }} disabled={isModalLoading}
                                />
                              </Stack>
                            </Tooltip>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!isModalLoading && userPermissions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={permissionTableColumns.length + 2} align="center" sx={{ py: 4 }}>
                            {selectedUser ? t('text.NoPermissionsDataFound') : t('text.SelectEmployeeToViewPermissions')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        userPermissions.map((row, index) => (
                          <TableRow key={row.menuId || `perm-${index}`} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }} >
                            <TableCell component="th" scope="row" sx={{ py: 0.5, px: 1 }}>{row.menuName}</TableCell>
                            <TableCell sx={{ py: 0.5, px: 1 }}>{row.parentMenuName}</TableCell>
                            {permissionTableColumns.map((col) => (
                              <TableCell key={col.key} align="center" sx={{ p: 0 }}>
                                <Checkbox
                                  size="small" checked={!!row[col.key as keyof UserPermissionData]}
                                  onChange={() => handleCheckboxChange(row.menuId, col.key as keyof UserPermissionData)}
                                  name={`${col.key}_${row.menuId}`}
                                  inputProps={{ 'aria-label': t('ariaLabel.togglePermission', { permission: col.label, menu: row.menuName }) }}
                                  sx={{ p: 0.5 }} disabled={isModalLoading}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </form>
          </Box>
        </Box>
      </Modal>
    </>
  );
}




















// import React, { useEffect, useState, useCallback } from "react";
// import {
//   Box,
//   Button,
//   Divider,
//   Modal,
//   Stack,
//   TextField,
//   Typography,
//   Paper,
//   Grid,
//   IconButton,
//   CircularProgress, // Keep CircularProgress
//   Autocomplete,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Checkbox,
//   Tooltip,
// } from "@mui/material";
// import AddCircleIcon from "@mui/icons-material/AddCircle";
// import EditIcon from "@mui/icons-material/Edit";
// import DeleteIcon from "@mui/icons-material/Delete";
// import CloseIcon from "@mui/icons-material/Close";
// import PrintIcon from "@mui/icons-material/Print";

// import { useNavigate, useLocation } from "react-router-dom";
// import axios from "axios";
// import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
// import HOST_URL from "../../../utils/Url";
// import { useTranslation } from "react-i18next";
// import { toast } from "react-toastify";
// import ToastApp from "../../../ToastApp";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";

// // --- Interfaces (keep as before) ---
// interface UserListData {
//   srno: number;
//   id: string;
//   userName: string;
//   roleName: string;
// }

// interface UserPermissionData {
//   id: string;
//   menuId: number;
//   parentId: number;
//   menuName: string;
//   parentMenuName: string;
//   isAdd: boolean;
//   isEdit: boolean;
//   isDel: boolean;
//   isView: boolean;
//   isPrint: boolean;
//   isExport: boolean;
//   isRelease: boolean;
//   isPost: boolean;
//   UserId: string;
// }

// interface UserOption {
//   useR_ID: string;
//   firsT_NAME: string;
//   middlE_NAME: string;
//   suR_NAME: string;
//   ranK_ID: string;
//   label?: string;
// }

// // --- Modal Style (keep as before) ---
// const modalStyle = {
//   position: "absolute" as "absolute",
//   top: "50%",
//   left: "50%",
//   transform: "translate(-50%, -50%)",
//   width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
//   maxWidth: '1200px',
//   maxHeight: '90vh',
//   bgcolor: "background.paper",
//   border: "1px solid #ccc",
//   borderRadius: 2,
//   boxShadow: 24,
//   p: { xs: 2, sm: 3, md: 4 },
//   display: 'flex',
//   flexDirection: 'column',
// };

// const modalHeaderStyle = {
//     flexShrink: 0,
//     borderBottom: 1,
//     borderColor: 'divider',
//     pb: 2,
//     mb: 2,
// };

// const modalContentStyle = {
//   overflowY: "auto",
//   flexGrow: 1,
//   '&::-webkit-scrollbar': { width: '8px' },
//   '&::-webkit-scrollbar-track': { bgcolor: '#f1f1f1', borderRadius: '4px' },
//   '&::-webkit-scrollbar-thumb': { bgcolor: '#bdbdbd', borderRadius: '4px', '&:hover': { bgcolor: '#a5a5a5' } },
// };


// // --- Helper Functions (keep as before) ---
// function createData(
//   srno: number, id: string, userName: string, roleName: string
// ): UserListData {
//   return { srno, id, userName, roleName };
// }

// function createUserPermissionData(
//   id: string, menuId: number, parentId: number, menuName: string, parentMenuName: string,
//   isAdd: boolean, isEdit: boolean, isDel: boolean, isView: boolean, isPrint: boolean,
//   isExport: boolean, isRelease: boolean, isPost: boolean, UserId: string
// ): UserPermissionData {
//   return { id, menuId, parentId, menuName, parentMenuName, isAdd, isEdit, isDel, isView, isPrint, isExport, isRelease, isPost, UserId };
// }

// // --- Component ---
// export default function UserPermissionMaster() {
//   const { t } = useTranslation();
//   const navigate = useNavigate();
//   const location = useLocation();

//   const [rows, setRows] = useState<UserListData[]>([]);
//   const [users, setUsers] = useState<UserOption[]>([]);
//   const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
//   const [userPermissions, setUserPermissions] = useState<UserPermissionData[]>([]);
//   const [isLoading, setIsLoading] = useState<boolean>(true);
//   const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
//   const [open, setOpen] = useState<boolean>(false);
//   const [editUserId, setEditUserId] = useState<string | null>(null);
//   const [deleteUserId, setDeleteUserId] = useState<string | null>(null);

//   // --- Data Fetching Callbacks (keep as before) ---
//   const getList = useCallback(async () => {
//     setIsLoading(true);
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/GetUserPermissionList`, { userId: "-1" });
//       if (response.data.isSuccess && Array.isArray(response.data.data)) {
//         const mappedData = response.data.data.map((item: any, index: number) =>
//           createData(index + 1, item.userId, item.userName, item.roleName)
//         );
//         setRows(mappedData);
//       } else {
//         toast.error(t("text.ErrorFetchingData") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
//         setRows([]);
//       }
//     } catch (error) {
//       console.error("API call failed (GetUserPermissionList):", error);
//       toast.error(t("text.ApiCallFailed"));
//       setRows([]);
//     } finally {
//       setIsLoading(false);
//     }
//   }, [t]);

//   const getEmployeeName = useCallback(async () => {
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}USER/GetUSER`, { useR_ID: "-1" });
//       if (response.data.isSuccess && Array.isArray(response.data.data)) {
//         const usersWithOptions = response.data.data.map((user: UserOption) => ({
//             ...user,
//             label: `${user.firsT_NAME || ''} ${user.middlE_NAME || ''} ${user.suR_NAME || ''}`.trim().replace(/\s+/g, ' ') || `User ID: ${user.useR_ID}`
//         }));
//         setUsers(usersWithOptions);
//       } else {
//         console.error("Error fetching users:", response.data.mesg);
//       }
//     } catch (error) {
//       console.error("API call failed (GetUSER):", error);
//     }
//   }, []);

//   const getPermissionsForRole = useCallback(async (roleId: string, targetUserId: string) => {
//     setIsModalLoading(true);
//     setUserPermissions([]);
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}RoleMaster/GetRoleSelectMenu`, { roleId });
//       if (response.data.isSuccess && response.data.data?.[0]?.rolePermission) {
//         const permissions = response.data.data[0].rolePermission;
//         const mappedPermissions = permissions.map((item: any) =>
//           createUserPermissionData(
//             roleId, item.menuId, item.parentId, item.menuName, item.parentMenuName,
//             item.isAdd ?? false, item.isEdit ?? false, item.isDel ?? false,
//             item.isView ?? false, item.isPrint ?? false, item.isExport ?? false,
//             item.isRelease ?? false, item.isPost ?? false, targetUserId
//           )
//         );
//         setUserPermissions(mappedPermissions);
//       } else {
//         toast.error(t("text.ErrorFetchingRolePermissions") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
//       }
//     } catch (error) {
//       console.error("API call failed (GetRoleSelectMenu):", error);
//       toast.error(t("text.ApiCallFailed"));
//     } finally {
//       setIsModalLoading(false);
//     }
//   }, [t]);

//   const getPermissionsForUser = useCallback(async (userId: string) => {
//     setIsModalLoading(true);
//     setUserPermissions([]);
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/GetUserPermissionMaster`, { userId });
//       if (response.data.isSuccess && Array.isArray(response.data.data)) {
//         const mappedPermissions = response.data.data.map((item: any) =>
//           createUserPermissionData(
//             item.roleId, item.menuId, item.parentId, item.menuName, item.parentMenuName,
//             item.isAdd ?? false, item.isEdit ?? false, item.isDel ?? false,
//             item.isView ?? false, item.isPrint ?? false, item.isExport ?? false,
//             item.isRelease ?? false, item.isPost ?? false, userId
//           )
//         );
//         const userToEdit = users.find(u => u.useR_ID === userId);
//         setSelectedUser(userToEdit || null);
//         setUserPermissions(mappedPermissions);
//       } else {
//         toast.error(t("text.ErrorFetchingUserPermissions") + (response.data.mesg ? `: ${response.data.mesg}` : ''));
//       }
//     } catch (error) {
//       console.error("API call failed (GetUserPermissionMaster):", error);
//       toast.error(t("text.ApiCallFailed"));
//     } finally {
//       setIsModalLoading(false);
//     }
//   }, [t, users]);

//   // --- Initial Load (keep as before) ---
//   useEffect(() => {
//     getList();
//     getEmployeeName();
//   }, [getList, getEmployeeName]);

//   // --- Event Handlers (keep as before, including handleSubmit, handleDelete etc.) ---
//   const handleUserChange = (event: React.SyntheticEvent, newValue: UserOption | null) => {
//     setSelectedUser(newValue);
//     if (newValue && !editUserId) {
//       getPermissionsForRole(newValue.ranK_ID, newValue.useR_ID);
//     } else if (!newValue) {
//         setUserPermissions([]);
//     }
//   };

//   const handleOpen = (userIdToEdit: string | null = null) => {
//     setEditUserId(userIdToEdit);
//     setSelectedUser(null);
//     setUserPermissions([]);
//     if (userIdToEdit) {
//         setOpen(true);
//         getPermissionsForUser(userIdToEdit);
//     } else {
//         setOpen(true);
//     }
//   };

//   const handleClose = () => {
//     setOpen(false);
//     setSelectedUser(null);
//     setUserPermissions([]);
//     setEditUserId(null);
//     setIsModalLoading(false);
//   };

//   const handleSelectAll = (permissionKey: keyof UserPermissionData, checked: boolean) => {
//      if (typeof checked !== 'boolean') return;
//     setUserPermissions((prevData) =>
//       prevData.map((item) => ({ ...item, [permissionKey]: checked }))
//     );
//   };

//   const handleCheckboxChange = (menuId: number, permissionKey: keyof UserPermissionData) => {
//     setUserPermissions((prevData) =>
//       prevData.map((item) =>
//         item.menuId === menuId ? { ...item, [permissionKey]: !item[permissionKey] } : item
//       )
//     );
//   };

//   const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
//     event.preventDefault();
//     if (!selectedUser) {
//       toast.warn(t("text.PleaseSelectEmployee"));
//       return;
//     }
//     if (userPermissions.length === 0) {
//         toast.warn(t("text.NoPermissionsAssigned"));
//         return;
//     }

//     const payload = {
//       userId: selectedUser.useR_ID,
//       userPermission: userPermissions,
//     };

//     setIsModalLoading(true); // Start loading indicator
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/AddUpdateUserPermissionMaster`, payload);
//       if (response.data.isSuccess) {
//         toast.success(response.data.mesg || (editUserId ? t("text.PermissionsUpdatedSuccess") : t("text.PermissionsAddedSuccess")));
//         handleClose();
//         getList();
//       } else {
//         toast.error(response.data.mesg || t("text.ErrorSavingPermissions"));
//       }
//     } catch (error) {
//       console.error("API call failed (AddUpdateUserPermissionMaster):", error);
//       toast.error(t("text.ApiCallFailed"));
//     } finally {
//       setIsModalLoading(false); // Stop loading indicator
//     }
//   };

//   const handleDeleteRequest = (userId: string) => {
//     setDeleteUserId(userId);
//     confirmDialog({
//       message: t("text.ConfirmDeleteUserPermissions"),
//       header: t("text.DeleteConfirmation"),
//       icon: "pi pi-info-circle",
//       acceptClassName: "p-button-danger",
//       accept: handleDeleteConfirm,
//       reject: handleDeleteReject,
//     });
//   };

//   const handleDeleteConfirm = async () => {
//     if (!deleteUserId) return;
//     // Add loading state specific to delete if needed
//     try {
//       const response = await axios.post(`${HOST_URL.HOST_URL}UserPermission/DeleteUserPermissionMaster`, { userId: deleteUserId });
//       if (response.data.isSuccess) {
//         toast.success(response.data.mesg || t("text.PermissionsDeletedSuccess"));
//         getList();
//       } else {
//         toast.error(response.data.mesg || t("text.ErrorDeletingPermissions"));
//       }
//     } catch (error) {
//       console.error("API call failed (DeleteUserPermissionMaster):", error);
//       toast.error(t("text.ApiCallFailed"));
//     } finally {
//       setDeleteUserId(null);
//     }
//   };

//   const handleDeleteReject = () => {
//     toast.info(t("text.DeleteOperationCancelled"), { autoClose: 3000 });
//     setDeleteUserId(null);
//   };


//   // --- DataGrid Columns (keep as before) ---
//   const columns: GridColDef<UserListData>[] = [
//     { field: "srno", headerName: t("text.SrNo"), minWidth: 80, align: 'center', headerAlign: 'center', filterable: false, sortable: false },
//     { field: 'userName', headerName: t('text.UserName'), minWidth: 250, flex: 1 },
//     { field: 'roleName', headerName: t('text.RoleName'), minWidth: 200, flex: 1 },
//     {
//         field: 'actions', headerName: t('text.Action'), minWidth: 130, sortable: false, filterable: false, disableColumnMenu: true, align: 'center', headerAlign: 'center',
//         renderCell: (params) => (
//           <Stack direction="row" spacing={1} justifyContent="center">
//             <Tooltip title={t('text.Edit')} arrow>
//               <IconButton onClick={() => handleOpen(params.row.id)} color="primary" size="small" aria-label={t('ariaLabel.Edit', { user: params.row.userName })}>
//                 <EditIcon fontSize="inherit" />
//               </IconButton>
//             </Tooltip>
//             <Tooltip title={t('text.delete')} arrow>
//               <IconButton onClick={() => handleDeleteRequest(params.row.id)} color="error" size="small" aria-label={t('ariaLabel.delete', { user: params.row.userName })}>
//                 <DeleteIcon fontSize="inherit"/>
//               </IconButton>
//             </Tooltip>
//           </Stack>
//         ),
//     },
//   ];

//   // --- Permission Table Columns Definition (keep as before) ---
//    const permissionTableColumns: { key: keyof UserPermissionData; label: string }[] = [
//         { key: "isAdd", label: t("text.add") }, { key: "isEdit", label: t("text.Edit") }, { key: "isDel", label: t("text.delete") },
//         { key: "isView", label: t("text.View") }, { key: "isPrint", label: t("text.Print") }, { key: "isExport", label: t("text.Export") },
//         { key: "isRelease", label: t("text.Release") }, { key: "isPost", label: t("text.Post") },
//     ];

//   // --- Render ---
//   return (
//     <>
//       <ToastApp />
//       <ConfirmDialog />

//       <Paper
//         sx={{
//           width: "100%", overflow: "hidden", p: { xs: 1, sm: 2, md: 3 }, mt: 2, mb: 2, boxShadow: 3,
//           "& .MuiDataGrid-columnHeaders": { bgcolor: `var(--grid-headerBackground, #1976d2)`, color: `var(--grid-headerColor, #fff)`, fontSize: '1rem', fontWeight: '600' },
//           "& .MuiDataGrid-toolbarContainer": { padding: '8px', borderBottom: '1px solid', borderColor: 'divider' },
//         }}
//       >
//         <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'medium' }}>
//           {t("text.UserPermissionMaster")}
//         </Typography>
//         <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
//           <Button
//             onClick={() => handleOpen()}
//             variant="contained"
//             startIcon={<AddCircleIcon />}
//             sx={{ bgcolor: 'var(--header-background, primary.main)', color: 'var(--header-color, primary.contrastText)', '&:hover': { bgcolor: 'var(--header-background-hover, primary.dark)'} }}
//             size="medium"
//           >
//             {t("text.add")}
//           </Button>
//         </Stack>

//         {isLoading ? (
//            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, p: 3 }}>
//               <CircularProgress />
//           </Box>
//         ) : (
//           <Box sx={{ height: 'auto', width: '100%' }}>
//             <DataGrid
//               rows={rows} columns={columns} getRowId={(row) => row.id} autoHeight
//               initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
//               pageSizeOptions={[10, 25, 50, 100]}
//               slots={{ toolbar: GridToolbar }}
//               slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 }, printOptions: { disableToolbarButton: true }, csvOptions: { disableToolbarButton: true } } }}
//               sx={{ border: 1, borderColor: 'divider', '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' }, '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' } }}
//               density="compact" disableRowSelectionOnClick
//             />
//           </Box>
//         )}
//       </Paper>

//       {/* --- Add/Edit Modal --- */}
//       <Modal open={open} onClose={handleClose} aria-labelledby="user-permission-modal-title">
//         <Box sx={modalStyle}>
//           <Stack direction="row" justifyContent="space-between" alignItems="center" sx={modalHeaderStyle}>
//             <Typography variant="h6" id="user-permission-modal-title" component="h2" sx={{ fontWeight: 'medium' }}>
//               {editUserId ? t("text.EditUserPermissions") : t("text.AddUserPermissions")}
//             </Typography>
//             <IconButton aria-label={t("ariaLabel.closeModal")} onClick={handleClose} size="small">
//               <CloseIcon />
//             </IconButton>
//           </Stack>

//           <Box sx={modalContentStyle}>
//             <form onSubmit={handleSubmit}>
//               <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
//                 <Grid item xs={12} md={6} lg={5}>
//                   <Autocomplete
//                     id="user-select-autocomplete" options={users} getOptionLabel={(option) => option.label || ''}
//                     value={selectedUser} onChange={handleUserChange} isOptionEqualToValue={(option, value) => option.useR_ID === value.useR_ID}
//                     renderInput={(params) => ( <TextField {...params} label={t("text.SelectEmployeeName")} variant="outlined" size="small" required /> )}
//                     disabled={!!editUserId || isModalLoading} fullWidth
//                   />
//                 </Grid>
//                 <Grid item xs={6} md={3} lg={2}>
//                   {/* --- Progress Button --- */}
//                   <Button
//                     type="submit"
//                     fullWidth
//                     variant="contained"
//                     sx={{
//                       bgcolor: 'var(--header-background, primary.main)',
//                       color: 'var(--header-color, primary.contrastText)',
//                       '&:hover': { bgcolor: 'var(--header-background-hover, primary.dark)'},
//                       // Ensure minimum height to accommodate spinner
//                       minHeight: '36.5px' // Adjust if needed based on theme/size
//                     }}
//                     disabled={isModalLoading || !selectedUser || userPermissions.length === 0} // Keep disabled logic
//                   >
//                     {isModalLoading ? (
//                       <CircularProgress size={24} color="inherit" /> // Show spinner when loading
//                     ) : (
//                       // Show text when not loading
//                       editUserId ? t("text.update") : t("text.save")
//                     )}
//                   </Button>
//                   {/* --- End Progress Button --- */}
//                 </Grid>
//                 <Grid item xs={6} md={3} lg={2}>
//                   <Button type="button" fullWidth variant="outlined" color="secondary" onClick={handleClose} disabled={isModalLoading}>
//                     {t("text.cancel")}
//                   </Button>
//                 </Grid>
//               </Grid>

//               <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 'medium' }}>
//                 {t("text.AssignPermissions")}
//               </Typography>

//               {isModalLoading && userPermissions.length === 0 ? (
//                 <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
//                   <CircularProgress />
//                 </Box>
//               ) : (
//                 <TableContainer component={Paper} elevation={1} sx={{ maxHeight: "calc(90vh - 260px)", border: 1, borderColor: 'divider' }}>
//                   <Table stickyHeader size="small" aria-label={t('ariaLabel.permissionsTable')}>
//                     <TableHead>
//                       <TableRow sx={{ '& th': { fontWeight: '600', bgcolor: `var(--grid-headerBackground, grey.200)`, color: `var(--grid-headerColor, #000)`, py: 0.5 } }}>
//                         <TableCell sx={{ textAlign: 'left', minWidth: 150, zIndex: 1100 }}>{t("text.MenuName")}</TableCell>
//                         <TableCell sx={{ textAlign: 'left', minWidth: 150, zIndex: 1100 }}>{t("text.ParentName")}</TableCell>
//                         {permissionTableColumns.map((col) => (
//                           <TableCell key={col.key} align="center" sx={{ px: { xs: 0.5, sm: 1 }, width: { xs: 65, sm: 80 }, zIndex: 1100 }}>
//                             <Tooltip title={t('tooltip.selectAll', { permission: col.label })} arrow placement="top">
//                               <Stack direction="column" alignItems="center" spacing={0}>
//                                 <Typography variant="caption" sx={{ lineHeight: 1.1, mb: -0.5 }}>{col.label}</Typography>
//                                 <Checkbox
//                                   size="small"
//                                   checked={userPermissions.length > 0 && userPermissions.every((item) => !!item[col.key])}
//                                   indeterminate={ userPermissions.length > 0 && !userPermissions.every((item) => !!item[col.key]) && userPermissions.some((item) => !!item[col.key]) }
//                                   onChange={(e) => handleSelectAll(col.key as keyof UserPermissionData, e.target.checked)}
//                                   inputProps={{ 'aria-label': t('ariaLabel.selectAllPermission', { permission: col.label }) }}
//                                   sx={{ p: 0.5 }} disabled={isModalLoading}
//                                 />
//                               </Stack>
//                             </Tooltip>
//                           </TableCell>
//                         ))}
//                       </TableRow>
//                     </TableHead>
//                     <TableBody>
//                       {!isModalLoading && userPermissions.length === 0 ? (
//                         <TableRow>
//                           <TableCell colSpan={permissionTableColumns.length + 2} align="center" sx={{ py: 4 }}>
//                             {selectedUser ? t('text.NoPermissionsDataFound') : t('text.SelectEmployeeToViewPermissions')}
//                           </TableCell>
//                         </TableRow>
//                       ) : (
//                         userPermissions.map((row, index) => (
//                           <TableRow key={row.menuId || `perm-${index}`} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }} >
//                             <TableCell component="th" scope="row" sx={{ py: 0.5, px: 1 }}>{row.menuName}</TableCell>
//                             <TableCell sx={{ py: 0.5, px: 1 }}>{row.parentMenuName}</TableCell>
//                             {permissionTableColumns.map((col) => (
//                               <TableCell key={col.key} align="center" sx={{ p: 0 }}>
//                                 <Checkbox
//                                   size="small" checked={!!row[col.key as keyof UserPermissionData]}
//                                   onChange={() => handleCheckboxChange(row.menuId, col.key as keyof UserPermissionData)}
//                                   name={`${col.key}_${row.menuId}`}
//                                   inputProps={{ 'aria-label': t('ariaLabel.togglePermission', { permission: col.label, menu: row.menuName }) }}
//                                   sx={{ p: 0.5 }} disabled={isModalLoading}
//                                 />
//                               </TableCell>
//                             ))}
//                           </TableRow>
//                         ))
//                       )}
//                     </TableBody>
//                   </Table>
//                 </TableContainer>
//               )}
//             </form>
//           </Box>
//         </Box>
//       </Modal>
//     </>
//   );
// }